/**
 * Search page facet + map-view state — a plain in-memory store (no
 * `persist`, unlike feed.store.ts's cross-session preferences), so it
 * naturally resets on a hard refresh but survives SearchPage unmounting
 * mid-session. That's what "View details" on a map pin needs: navigating to
 * the post/group detail page and back (SearchResultsMap → PostDetailPage →
 * navigate(-1)) unmounts SearchPage entirely, and only `q`/`type` were ever
 * recoverable afterward (they're synced to the URL search params). Every
 * other facet, plus the results map's collapsed/expanded state, center, and
 * zoom, reset to defaults on remount unless they live here instead of a
 * component-local useState.
 */
import { create } from 'zustand'
import type { Language, Region } from '@olimhub/shared'

export type LocationMode = 'off' | 'profile' | 'address' | 'map_center'

interface SearchFacetState {
  language: Language | 'all'
  helpStatus: 'all' | 'open' | 'resolved'
  categoryId: string | null
  startsAfter: string
  startsBefore: string
  selectedGroup: { slug: string; name: string } | null
  groupQuery: string
  urgentOnly: boolean
  accountLanguages: Language[]
  groupRegion: Region | 'any'
  groupOpenFilter: 'all' | 'open' | 'closed'
  // Location facet (Event/Action/Groups) — deliberately NOT reset by
  // resetTypeFacets below, same as SearchPage's own selectType() never
  // resets it — the spec wants radius/mode/reference point to stay put
  // when moving between Event/Action/Groups.
  locationMode: LocationMode
  radiusKm: number
  addressText: string
  referenceLat: number | null
  referenceLng: number | null
  // Results map (spec-search-map-merge.md) — collapsed by default, not
  // reset on type switch either. mapCenter/mapZoom are only ever written by
  // SearchResultsMap's own moveend/zoomend listener (null until the user
  // actually opens/moves the map at least once), read back as its restored
  // initial view instead of always recomputing profile-location-or-Haifa.
  mapExpanded: boolean
  mapCenter: [number, number] | null
  mapZoom: number | null

  setLanguage: (v: Language | 'all') => void
  setHelpStatus: (v: 'all' | 'open' | 'resolved') => void
  setCategoryId: (v: string | null) => void
  setStartsAfter: (v: string) => void
  setStartsBefore: (v: string) => void
  setSelectedGroup: (v: { slug: string; name: string } | null) => void
  setGroupQuery: (v: string) => void
  setUrgentOnly: (v: boolean) => void
  setAccountLanguages: (v: Language[]) => void
  setGroupRegion: (v: Region | 'any') => void
  setGroupOpenFilter: (v: 'all' | 'open' | 'closed') => void
  setLocationMode: (v: LocationMode) => void
  setRadiusKm: (v: number) => void
  setAddressText: (v: string) => void
  setReferenceLat: (v: number | null) => void
  setReferenceLng: (v: number | null) => void
  setMapExpanded: (v: boolean) => void
  setMapView: (center: [number, number], zoom: number) => void
  // Bundled reset for a type switch — see SearchPage's selectType(). Leaves
  // the location facet and map view untouched, per the comment above.
  resetTypeFacets: () => void
}

export const useSearchStore = create<SearchFacetState>()((set) => ({
  language: 'all',
  helpStatus: 'all',
  categoryId: null,
  startsAfter: '',
  startsBefore: '',
  selectedGroup: null,
  groupQuery: '',
  urgentOnly: false,
  accountLanguages: [],
  groupRegion: 'any',
  groupOpenFilter: 'all',
  locationMode: 'off',
  radiusKm: 10,
  addressText: '',
  referenceLat: null,
  referenceLng: null,
  mapExpanded: false,
  mapCenter: null,
  mapZoom: null,

  setLanguage: (v) => set({ language: v }),
  setHelpStatus: (v) => set({ helpStatus: v }),
  setCategoryId: (v) => set({ categoryId: v }),
  setStartsAfter: (v) => set({ startsAfter: v }),
  setStartsBefore: (v) => set({ startsBefore: v }),
  setSelectedGroup: (v) => set({ selectedGroup: v }),
  setGroupQuery: (v) => set({ groupQuery: v }),
  setUrgentOnly: (v) => set({ urgentOnly: v }),
  setAccountLanguages: (v) => set({ accountLanguages: v }),
  setGroupRegion: (v) => set({ groupRegion: v }),
  setGroupOpenFilter: (v) => set({ groupOpenFilter: v }),
  setLocationMode: (v) => set({ locationMode: v }),
  setRadiusKm: (v) => set({ radiusKm: v }),
  setAddressText: (v) => set({ addressText: v }),
  setReferenceLat: (v) => set({ referenceLat: v }),
  setReferenceLng: (v) => set({ referenceLng: v }),
  setMapExpanded: (v) => set({ mapExpanded: v }),
  setMapView: (center, zoom) => set({ mapCenter: center, mapZoom: zoom }),
  resetTypeFacets: () => set({
    helpStatus: 'all',
    urgentOnly: false,
    categoryId: null,
    startsAfter: '',
    startsBefore: '',
    selectedGroup: null,
    groupQuery: '',
    accountLanguages: [],
    groupRegion: 'any',
    groupOpenFilter: 'all',
  }),
}))
